<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Upload,
  GraduationCap,
  UsersRound,
  CreditCard,
} from "@lucide/vue";
import { api } from "../api";
import { useSessionStore } from "../stores/session";
import PageHeader from "../components/PageHeader.vue";
import TablePager from "../components/TablePager.vue";
import UiDrawer from "../ui/UiDrawer.vue";
import UiSelect from "../ui/UiSelect.vue";
import UiTabs from "../ui/UiTabs.vue";
import UiDateInput from "../ui/UiDateInput.vue";
import UiToast from "../ui/UiToast.vue";
import UiButton from "../ui/UiButton.vue";
import UiConfirmDrawer from "../ui/UiConfirmDrawer.vue";
import UiPhoneInput from "../ui/UiPhoneInput.vue";
import UiStudentAvatar from "../ui/UiStudentAvatar.vue";
const session = useSessionStore();
const rows = ref([]);
const loading = ref(true);
const search = ref("");
const status = ref("active");
const page = ref(1);
const perPage = 12;
const modal = ref(false);
const activeTab = ref("student");
const saving = ref(false);
const deleting = ref(false);
const deleteTarget = ref(null);
const toast = ref("");
const toastType = ref("success");
const blank = () => ({
  id: null,
  schoolId: session.selectedSchoolId || session.user?.school_id || "",
  studentNo: "",
  firstName: "",
  lastName: "",
  className: "",
  phone: "",
  address: "",
  validUntil: "",
  guardianName: "",
  guardianPhone: "",
  guardianEmail: "",
  guardianAddress: "",
  guardianUsername: "",
  guardianPassword: "",
  cardUid: "",
  status: "active",
  photoData: "",
  photoPath: "",
});
const form = reactive(blank());
const schoolOptions = computed(() =>
  session.schools.map((school) => ({
    value: String(school.id),
    label: school.name,
    description: school.code || "Erva Spor Okulu",
  })),
);
const statusOptions = [
  { value: "active", label: "Aktif", description: "Geçiş yetkisi açık" },
  { value: "passive", label: "Pasif", description: "Geçiş yetkisi kapalı" },
];
const formTabs = [
  { value: "student", label: "Öğrenci", icon: GraduationCap },
  { value: "guardian", label: "Veli", icon: UsersRound },
  { value: "access", label: "Kart ve erişim", icon: CreditCard },
];
const filtered = computed(() =>
  rows.value.filter(
    (row) =>
      row.status === status.value &&
      `${row.first_name} ${row.last_name} ${row.student_no || ""} ${row.class_name || ""} ${row.guardian_name || ""}`
        .toLocaleLowerCase("tr-TR")
        .includes(search.value.toLocaleLowerCase("tr-TR")),
  ),
);
const paged = computed(() =>
  filtered.value.slice((page.value - 1) * perPage, page.value * perPage),
);
function show(msg, type = "success") {
  toast.value = msg;
  toastType.value = type;
  setTimeout(() => (toast.value = ""), 3500);
}
async function load() {
  loading.value = true;
  try {
    const q = session.selectedSchoolId
      ? `?schoolId=${session.selectedSchoolId}`
      : "";
    rows.value = (await api(`/api/students${q}`)).students || [];
  } catch (e) {
    show(e.message, "error");
  } finally {
    loading.value = false;
  }
}
function open(row = null) {
  Object.assign(form, blank());
  activeTab.value = "student";
  if (row)
    Object.assign(form, {
      id: row.id,
      schoolId: String(row.school_id),
      studentNo: row.student_no || "",
      firstName: row.first_name,
      lastName: row.last_name,
      className: row.class_name || "",
      phone: row.phone || "",
      address: row.address || "",
      validUntil: row.valid_until || "",
      guardianName: row.guardian_name || "",
      guardianPhone: row.guardian_phone || "",
      guardianEmail: row.guardian_email || "",
      guardianAddress: row.guardian_address || "",
      guardianUsername: row.parent_username || "",
      status: row.status,
      photoPath: row.photo_path || "",
    });
  modal.value = true;
}
function photo(event) {
  const file = event.target.files?.[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = () => (form.photoData = String(reader.result));
  reader.readAsDataURL(file);
}
async function save() {
  saving.value = true;
  try {
    const path = form.id ? `/api/students/${form.id}` : "/api/students";
    await api(path, {
      method: form.id ? "PUT" : "POST",
      body: JSON.stringify(form),
    });
    show(form.id ? "Öğrenci güncellendi." : "Öğrenci kaydedildi.");
    modal.value = false;
    await load();
  } catch (e) {
    show(e.message, "error");
  } finally {
    saving.value = false;
  }
}
async function remove(row) {
  deleteTarget.value = row;
}
async function confirmRemove() {
  if (!deleteTarget.value) return;
  deleting.value = true;
  try {
    await api(`/api/students/${deleteTarget.value.id}`, { method: "DELETE" });
    show("Öğrenci silindi.");
    deleteTarget.value = null;
    await load();
  } catch (e) {
    show(e.message, "error");
  } finally {
    deleting.value = false;
  }
}
const onRefresh = () => load();
onMounted(() => {
  load();
  window.addEventListener("lopiy:refresh", onRefresh);
});
onBeforeUnmount(() => window.removeEventListener("lopiy:refresh", onRefresh));
</script>
<template>
  <PageHeader
    title="Öğrenciler"
    description="Erva Spor Okulları öğrenci, veli ve kart ilişkilerini yönetin."
    ><button v-if="!session.isParent" class="btn-primary" @click="open()">
      <Plus class="size-4" />Öğrenci Ekle
    </button></PageHeader
  >
  <section class="panel overflow-hidden">
    <div class="toolbar">
      <div class="flex rounded-md bg-slate-100 p-1">
        <button
          v-for="item in [
            { v: 'active', l: 'Aktif' },
            { v: 'passive', l: 'Pasif' },
          ]"
          :key="item.v"
          :class="[
            'h-8 rounded px-4 text-xs font-bold',
            status === item.v
              ? 'bg-white text-ink-950 shadow-sm'
              : 'text-slate-500',
          ]"
          @click="
            status = item.v;
            page = 1;
          "
        >
          {{ item.l }}
        </button>
      </div>
      <label class="relative w-full sm:w-72"
        ><Search class="absolute left-3 top-2.5 size-4 text-slate-400" /><input
          v-model="search"
          class="input pl-9"
          placeholder="Öğrenci, sınıf veya veli ara"
          @input="page = 1"
      /></label>
    </div>
    <div class="overflow-x-auto">
      <table class="data-table">
        <thead>
          <tr>
            <th>Öğrenci</th>
            <th>Okul</th>
            <th>Numara</th>
            <th>Sınıf</th>
            <th>Veli</th>
            <th>Telefon</th>
            <th>Geçerlilik</th>
            <th>Durum</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in paged" :key="row.id">
            <td>
              <div class="flex items-center gap-3">
                <UiStudentAvatar
                  :src="row.photo_path"
                  :first-name="row.first_name"
                  :last-name="row.last_name"
                />
                <strong class="whitespace-nowrap text-ink-900"
                  >{{ row.first_name }} {{ row.last_name }}</strong
                >
              </div>
            </td>
            <td>{{ row.school_name }}</td>
            <td>{{ row.student_no || "-" }}</td>
            <td>{{ row.class_name || "-" }}</td>
            <td>{{ row.guardian_name || "-" }}</td>
            <td class="whitespace-nowrap">{{ row.guardian_phone || "-" }}</td>
            <td>{{ row.valid_until || "Süresiz" }}</td>
            <td>
              <span
                :class="
                  row.status === 'active' ? 'chip-success' : 'chip-neutral'
                "
                >{{ row.status === "active" ? "Aktif" : "Pasif" }}</span
              >
            </td>
            <td>
              <div class="flex justify-end gap-1">
                <button class="icon-btn size-8" @click="open(row)">
                  <Pencil class="size-3.5" /></button
                ><button
                  v-if="!session.isParent"
                  class="icon-btn size-8 hover:text-red-600"
                  @click="remove(row)"
                >
                  <Trash2 class="size-3.5" />
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="!loading && !paged.length">
            <td colspan="9" class="py-16 text-center text-slate-400">
              Bu filtrede öğrenci bulunamadı.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <TablePager
      v-model:page="page"
      :total="filtered.length"
      :per-page="perPage"
    />
  </section>
  <UiDrawer
    :open="modal"
    :title="form.id ? 'Öğrenciyi düzenle' : 'Yeni öğrenci kaydı'"
    description="Öğrenci, veli ve kart bilgilerini tek akışta tamamlayın."
    size="lg"
    @close="modal = false"
  >
    <UiTabs v-model="activeTab" :items="formTabs" class="mb-6" />
    <form id="student-form" @submit.prevent="save">
      <div v-show="activeTab === 'student'" class="grid gap-5 md:grid-cols-2">
        <label v-if="session.isSuper" class="field md:col-span-2">
          <span class="field-label">Okul</span>
          <UiSelect
            v-model="form.schoolId"
            :options="schoolOptions"
            searchable
            search-placeholder="Okul ara..."
          />
        </label>
        <label class="field"
          ><span class="field-label">Ad</span
          ><input
            v-model="form.firstName"
            class="input"
            autocomplete="given-name"
            required
        /></label>
        <label class="field"
          ><span class="field-label">Soyad</span
          ><input
            v-model="form.lastName"
            class="input"
            autocomplete="family-name"
            required
        /></label>
        <label class="field"
          ><span class="field-label">Öğrenci no</span
          ><input v-model="form.studentNo" class="input"
        /></label>
        <label class="field"
          ><span class="field-label">Sınıf</span
          ><input v-model="form.className" class="input"
        /></label>
        <label class="field"
          ><span class="field-label">Öğrenci telefonu</span
          ><UiPhoneInput v-model="form.phone"
        /></label>
        <label class="field"
          ><span class="field-label">Geçerlilik tarihi</span
          ><UiDateInput v-model="form.validUntil"
        /></label>
        <label class="field md:col-span-2"
          ><span class="field-label">Adres</span
          ><textarea v-model="form.address" class="textarea" />
        </label>
        <label class="field md:col-span-2">
          <span class="field-label">Fotoğraf</span>
          <span
            class="flex h-11 cursor-pointer items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 text-sm font-semibold text-slate-500 hover:border-brand-500 hover:text-brand-700"
          >
            <Upload class="size-4" />Görsel seç<input
              type="file"
              accept="image/*"
              class="hidden"
              @change="photo"
            />
          </span>
        </label>
      </div>
      <div v-show="activeTab === 'guardian'" class="grid gap-5 md:grid-cols-2">
        <label class="field md:col-span-2"
          ><span class="field-label">Veli adı soyadı</span
          ><input v-model="form.guardianName" class="input"
        /></label>
        <label class="field"
          ><span class="field-label">Veli telefonu</span
          ><UiPhoneInput v-model="form.guardianPhone"
        /></label>
        <label class="field"
          ><span class="field-label">Veli e-posta</span
          ><input
            v-model="form.guardianEmail"
            type="email"
            class="input"
            autocomplete="email"
        /></label>
        <label v-if="!session.isParent" class="field"
          ><span class="field-label">Panel kullanıcı adı</span
          ><input v-model="form.guardianUsername" class="input"
        /></label>
        <label v-if="!session.isParent" class="field"
          ><span class="field-label">Panel şifresi</span
          ><input v-model="form.guardianPassword" type="password" class="input"
        /></label>
        <label class="field md:col-span-2"
          ><span class="field-label">Veli adresi</span
          ><textarea v-model="form.guardianAddress" class="textarea" />
        </label>
      </div>
      <div v-show="activeTab === 'access'" class="grid gap-5 md:grid-cols-2">
        <label v-if="!form.id && !session.isParent" class="field md:col-span-2"
          ><span class="field-label">Kart UID</span
          ><input
            v-model="form.cardUid"
            class="input font-mono uppercase"
            placeholder="Örn. 59A4E306"
        /></label>
        <label v-if="!session.isParent" class="field md:col-span-2"
          ><span class="field-label">Kayıt durumu</span
          ><UiSelect v-model="form.status" :options="statusOptions"
        /></label>
        <div
          class="md:col-span-2 rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm leading-6 text-sky-800"
        >
          Kart UID alanı boş bırakılırsa öğrenci kaydı kart atanmadan
          oluşturulur. Kartı daha sonra Kartlar sayfasından bağlayabilirsiniz.
        </div>
      </div>
    </form>
    <template #footer>
      <UiButton variant="secondary" @click="modal = false">Vazgeç</UiButton>
      <UiButton type="submit" form="student-form" :loading="saving"
        >Öğrenciyi Kaydet</UiButton
      >
    </template>
  </UiDrawer>
  <UiConfirmDrawer
    :open="Boolean(deleteTarget)"
    title="Öğrenciyi sil"
    :description="
      deleteTarget
        ? `${deleteTarget.first_name} ${deleteTarget.last_name} kaydı ve ilişkili erişim bilgileri silinecek.`
        : ''
    "
    confirm-label="Öğrenciyi Sil"
    :loading="deleting"
    @close="deleteTarget = null"
    @confirm="confirmRemove"
  />
  <UiToast :message="toast" :type="toastType" @close="toast = ''" />
</template>
